/* eslint-disable */
import { useTranslation } from '@gimlite/common';
import { Color, Loading, Write } from '@gimlite/ui-v1';
import { observer } from 'mobx-react-lite';
import { createContext, useContext, useMemo } from 'react';
import './progress.component.scss';

export declare namespace ProgressType {
  type Props = {
    className?: string;
    config: {
      color: Extract<Color, 'success' | 'warn' | 'error' | 'yang'>;
      name: string | React.ReactNode;
      value: number;
      isReference?: boolean;
    };
  };
}

export const Progress = observer<ProgressType.Props>(
  ({ config: { color, name, value, isReference = false } }) => {
    const { total } = useContext(ProgressContext);
    const pourcent = total ? ((value / total) * 100).toFixed(2) : '0';

    return (
      <div className={`progress progress--background--${color}`}>
        <div className="progress__name">
          {typeof name === 'string' ? (
            <Write
              data={{ item: name }}
              config={{ mode: 'value-medium', color: 'label', wrap: false }}
            />
          ) : (
            name
          )}
        </div>

        <div style={{ width: `${pourcent}%` }} className={`progress__bar`}>
          <div
            className={`progress__bar__color progress__bar__color--${
              isReference ? 'reference' : 'data'
            }`}
          />
          <div className="progress__bar__count">
            <Write
              className="progress__bar__count__text"
              data={{
                item: `${value} ${isReference ? '=' : '/'} ${pourcent}%`,
              }}
              config={{ mode: 'value-small', wrap: false }}
            />
          </div>
        </div>
      </div>
    );
  },
);

export declare namespace ProgressGroupType {
  type Props = {
    className?: string;
    config: {
      total?: number | null;
    };
    children: React.ReactNode | React.ReactNode[];
  };

  namespace Config {
    type Total = number;
  }
}

export const ProgressGroup = observer(
  ({
    className = '',
    children,
    config: { total },
  }: ProgressGroupType.Props) => {
    const { t } = useTranslation();

    const overWidth = useMemo(() => {
      const theGreatestValue = (
        Array.isArray(children) ? children : [children]
      ).reduce((acc: number, element: any) => {
        const currentValue = element!.props.config.value;
        return currentValue > acc ? currentValue : acc;
      }, 0);

      return theGreatestValue <= (total || 0)
        ? 0
        : ((theGreatestValue - (total || 0)) / theGreatestValue) * 100;
    }, [children, total]);

    const leftStyle = useMemo(() => {
      return {
        width: `calc(${100 - overWidth}%)`,
        paddingRight: !!overWidth ? 0 : '10px',
        paddingLeft: '10px',
      };
    }, [overWidth]);

    const rigthStyle = useMemo(() => {
      return {
        display: !!overWidth ? 'block' : 'none',
        width: `calc(${overWidth}% + ${4}rem)`,
      };
    }, [overWidth]);

    return (
      <ProgressContext.Provider value={{ total }}>
        {total === undefined ? (
          <div className="progressgroup--loading">
            <Loading />
          </div>
        ) : (
          <div className={`progressgroup ${className}`}>
            <div className="progressgroup__contain">
              <div style={leftStyle} className="progressgroup__contain__bars">
                {children}
              </div>
              <div
                style={rigthStyle}
                className="progressgroup__contain__placeholder"
              ></div>
            </div>
            <div className="progressgroup__background">
              <div
                style={leftStyle}
                className="progressgroup__background__placeholder"
              ></div>
              <div
                style={rigthStyle}
                className="progressgroup__background__over"
              >
                <span className="progressgroup__background__over__text">
                  {t('overbookArea').toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        )}
      </ProgressContext.Provider>
    );
  },
);

const ProgressContext = createContext<{
  total?: ProgressGroupType.Config.Total | null;
}>({
  total: 0,
});
